import type { ActionFunctionArgs, MetaFunction } from "@remix-run/node";
import { json } from "@remix-run/node";
import { useFetcher } from "@remix-run/react";
import { useEffect, useState } from "react";
import sgMail from "@sendgrid/mail";
import { LanguageSwitcher } from "~/components/language-switcher";
import { useI18n } from "~/lib/i18n/use-i18n";

export const meta: MetaFunction = () => {
  return [
    { title: "ÉNERVIE - The Fastest Path to Clean Energy" },
    {
      name: "description",
      content: "Professional solar installations that pay for themselves",
    },
  ];
};

// Server-side action to send email via SendGrid
export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const name = formData.get("name") as string;
  const email = formData.get("email") as string;
  const phone = formData.get("phone") as string;
  const message = formData.get("message") as string;
  const type = formData.get("type") as string; // "contact" or "newsletter"

  if (type === "newsletter") {
    if (!email) {
      return json(
        { success: false, error: "Email required" },
        { status: 400 }
      );
    }
    // For now, just return success (you can integrate with an email service later)
    return json({ success: true, type: "newsletter" });
  }

  if (!name || !email || !message) {
    return json(
      { success: false, error: "Missing required fields" },
      { status: 400 }
    );
  }

  try {
    sgMail.setApiKey(process.env.SENDGRID_API_KEY!);

    await sgMail.send({
      to: "contact@enervie.tn",
      from: "website@enervie.tn",
      replyTo: email,
      subject: `Contact Form: ${name}`,
      text: `Name: ${name}\nEmail: ${email}\nPhone: ${phone || 'Not provided'}\n\nMessage:\n${message}`,
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #1565c0; border-bottom: 3px solid #4caf50; padding-bottom: 10px;">
            New Contact Form Submission
          </h2>
          <div style="background-color: #f5f5f5; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <p style="margin: 10px 0;"><strong style="color: #1565c0;">Name:</strong> ${name}</p>
            <p style="margin: 10px 0;"><strong style="color: #1565c0;">Email:</strong> 
              <a href="mailto:${email}" style="color: #4caf50;">${email}</a>
            </p>
            ${phone ? `<p style="margin: 10px 0;"><strong style="color: #1565c0;">Phone:</strong> <a href="tel:${phone}" style="color: #4caf50;">${phone}</a></p>` : ''}
          </div>
          <div style="background-color: #fff; padding: 20px; border: 2px solid #e8f5e9; border-radius: 8px;">
            <h3 style="color: #1565c0; margin-top: 0;">Message:</h3>
            <p style="white-space: pre-wrap; line-height: 1.6;">${message}</p>
          </div>
          <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e0e0e0; text-align: center; color: #666;">
            <p style="margin: 0;">ÉNERVIE - Nouvelle Énergie, Nouvelle Vie</p>
          </div>
        </div>
      `,
    });

    return json({ success: true, type: "contact" });
  } catch (error) {
    console.error("SendGrid error:", error);
    return json(
      { success: false, error: "Failed to send email" },
      { status: 500 }
    );
  }
}

export default function Index() {
  const { t, isRTL } = useI18n();
  const fetcher = useFetcher<{ success: boolean; error?: string; type?: string }>();
  const newsletterFetcher = useFetcher<{ success: boolean; error?: string; type?: string }>();
  const [showSuccess, setShowSuccess] = useState(false);
  const [showNewsletterSuccess, setShowNewsletterSuccess] = useState(false);
  const [submittedName, setSubmittedName] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState("");
  
  // Lights feature state
  const [lightsOn, setLightsOn] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  
  // Scroll effect state
  const [scrollScale, setScrollScale] = useState(1);
  
  const cardBg = "rgba(232, 245, 233, 0.6)";
  const formBg = "rgba(255, 255, 255, 0.9)";

  useEffect(() => {
    const hasVisited = localStorage.getItem("enervie_lights_on");
    if (hasVisited === "true") {
      setLightsOn(true);
      setShowWelcome(false);
    }
  }, []);

  // Show success message when form is submitted
  useEffect(() => {
    if (fetcher.data?.success && fetcher.data?.type === "contact") {
      // Capture the values before they disappear
      const name = (fetcher.formData?.get("name") as string) || "";
      const email = (fetcher.formData?.get("email") as string) || "";
      setSubmittedName(name);
      setSubmittedEmail(email);
      
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 10000);
    }
  }, [fetcher.data]);

  // Handle turning on the lights
  const handleTurnOnLights = () => {
    setLightsOn(true);
    setTimeout(() => setShowWelcome(false), 1000);
    localStorage.setItem("enervie_lights_on", "true");
  };

  // Show success message when form is submitted
  useEffect(() => {
    if (fetcher.data?.success && fetcher.data?.type === "contact") {
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 10000);
    }
  }, [fetcher.data]);

  // Show newsletter success
  useEffect(() => {
    if (newsletterFetcher.data?.success && newsletterFetcher.data?.type === "newsletter") {
      setShowNewsletterSuccess(true);
      setTimeout(() => setShowNewsletterSuccess(false), 5000);
    }
  }, [newsletterFetcher.data]);
  
  // Lock scrolling until lights are turned on
  useEffect(() => {
    if (showWelcome && !lightsOn) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showWelcome, lightsOn]);
  // Video shrink effect on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const heroHeight = 700; // Match your min-h-[700px]
      
      // Calculate scale: starts at 1, shrinks to 0.7 as you scroll past hero
      const scale = Math.max(0.7, 1 - (scrollY / heroHeight) * 0.3);
      setScrollScale(scale);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isSubmitting = fetcher.state === "submitting";
  const isNewsletterSubmitting = newsletterFetcher.state === "submitting";

  return (
    <div 
      className={`min-h-screen relative ${lightsOn ? 'lights-on' : ''}`}
      dir={isRTL ? "rtl" : "ltr"}
      style={!lightsOn ? { filter: 'brightness(0.3)' } : {}}
    >
      {/* Dark Overlay & Welcome Message */}
      {showWelcome && (
        <div
          onClick={handleTurnOnLights}
          className={`fixed inset-0 z-[100] flex items-start justify-center pt-32 transition-opacity duration-1000 cursor-pointer ${
            lightsOn ? "opacity-0 pointer-events-none" : "opacity-100"
          }`}
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          }}
        >
          <div className="text-center px-6 space-y-6 animate-pulse">
            <div className="relative w-64 h-64 mx-auto mb-4">
              {/* Animated Sun SVG */}
              <svg
                className="w-full h-full animate-sun-glow"
                viewBox="0 0 100 100"
                xmlns="http://www.w3.org/2000/svg"
              >
                {/* Sun rays (rotating) */}
                <g className="animate-sun-rotate" style={{ transformOrigin: "50% 50%" }}>
                  <line x1="50" y1="5" x2="50" y2="20" stroke="#FFC107" strokeWidth="3" strokeLinecap="round" />
                  <line x1="73.5" y1="13.5" x2="64.5" y2="22.5" stroke="#FFC107" strokeWidth="3" strokeLinecap="round" />
                  <line x1="95" y1="50" x2="80" y2="50" stroke="#FFC107" strokeWidth="3" strokeLinecap="round" />
                  <line x1="73.5" y1="86.5" x2="64.5" y2="77.5" stroke="#FFC107" strokeWidth="3" strokeLinecap="round" />
                  <line x1="50" y1="95" x2="50" y2="80" stroke="#FFC107" strokeWidth="3" strokeLinecap="round" />
                  <line x1="26.5" y1="86.5" x2="35.5" y2="77.5" stroke="#FFC107" strokeWidth="3" strokeLinecap="round" />
                  <line x1="5" y1="50" x2="20" y2="50" stroke="#FFC107" strokeWidth="3" strokeLinecap="round" />
                  <line x1="26.5" y1="13.5" x2="35.5" y2="22.5" stroke="#FFC107" strokeWidth="3" strokeLinecap="round" />
                </g>
                
                {/* Sun center (static) */}
                <circle cx="50" cy="50" r="20" fill="#FFC107" />
                <circle cx="50" cy="50" r="18" fill="#FFD54F" />
              </svg>
            </div>
            
            <div className="space-y-6">
              <h2 
                className="text-4xl md:text-5xl font-bold text-white"
                style={{
                  textShadow: '0 4px 12px rgba(0, 0, 0, 0.9), 0 2px 6px rgba(0, 0, 0, 1)'
                }}
              >
                {t("welcome.title")}
              </h2>
              <p 
                className="text-xl md:text-2xl font-bold text-white"
                style={{
                  textShadow: '0 3px 10px rgba(0, 0, 0, 0.9), 0 2px 5px rgba(0, 0, 0, 1)'
                }}
              >
                {t("welcome.clickAnywhere")}
              </p>
              <div className="flex justify-center mt-8">
                <div 
                  className="px-8 py-4 bg-[#4caf50] text-white text-lg rounded-full font-bold hover:bg-[#45a049] transition-colors animate-bounce shadow-2xl border-2 border-white/30"
                  style={{
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)'
                  }}
                >
                  {t("welcome.clickButton")}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white/95 backdrop-blur shadow-md sticky top-0 z-50 border-b border-pale-green">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`flex justify-between items-center h-20 ${
              isRTL ? "flex-row-reverse" : ""
            }`}
          >
            <div className="flex items-center">
              <button
                onClick={handleTurnOnLights}
                className="transition-transform hover:scale-110 focus:outline-none focus:ring-4 focus:ring-[#4caf50] rounded-lg"
                aria-label={t("welcome.clickLogo")}
              >
                <img
                  src="/logo.png"
                  alt="ÉNERVIE Logo"
                  className="h-16 w-auto object-contain cursor-pointer"
                />
              </button>
            </div>
            <div className="flex items-center gap-6">
              <ul className={`flex ${isRTL ? "flex-row-reverse" : ""} gap-8`}>
                <li>
                  <a
                    href="#accueil"
                    className="text-[#1565c0] font-medium hover:text-[#4caf50] transition-colors"
                  >
                    {t("nav.home")}
                  </a>
                </li>
                <li>
                  <a
                    href="#services"
                    className="text-[#1565c0] font-medium hover:text-[#4caf50] transition-colors"
                  >
                    {t("nav.services")}
                  </a>
                </li>
                <li>
                  <a
                    href="#results"
                    className="text-[#1565c0] font-medium hover:text-[#4caf50] transition-colors"
                  >
                    {t("nav.results")}
                  </a>
                </li>
                <li>
                  <a
                    href="#apropos"
                    className="text-[#1565c0] font-medium hover:text-[#4caf50] transition-colors"
                  >
                    {t("nav.about")}
                  </a>
                </li>
                <li>
                  <a
                    href="#contact"
                    className="text-[#1565c0] font-medium hover:text-[#4caf50] transition-colors"
                  >
                    {t("nav.contact")}
                  </a>
                </li>
              </ul>
              <LanguageSwitcher />
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section - Both sides fill completely */}
      <section id="accueil" className="relative">
        <div className="w-full">
          <div className="w-full min-h-[600px] lg:min-h-[700px] flex flex-col lg:flex-row">
            {/* LEFT SIDE - Banner (fills completely like the video) */}
            <div className="w-full lg:w-1/2 min-h-[400px] lg:min-h-[700px] relative overflow-hidden bg-gradient-to-br from-[#e8f5e9] to-white">
              <img
                src="/banner.png"
                alt="ÉNERVIE Banner"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
            
            {/* RIGHT SIDE - Video (fills completely with rounded shrink effect) */}
            <div className="w-full lg:w-1/2 min-h-[400px] lg:min-h-[700px] relative overflow-hidden flex items-center justify-center"
                 style={{ backgroundColor: '#DFE5D3' }}>
              <video
                src="/solar.mp4"
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover transition-all duration-500 ease-out"
                style={{ 
                  transform: `scale(${scrollScale})`,
                  transformOrigin: 'center center',
                  borderRadius: `${(1 - scrollScale) * 80}px`
                }}
              >
                Your browser does not support the video tag.
              </video>
              
              {/* Optional gradient overlay */}
              <div 
                className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent pointer-events-none transition-all duration-500 ease-out"
                style={{ 
                  transform: `scale(${scrollScale})`,
                  transformOrigin: 'center center',
                  borderRadius: `${(1 - scrollScale) * 80}px`
                }}
              ></div>
            </div>
          </div>
        </div>
        <div className="text-center py-20 px-4 space-y-8">
          <div className="flex justify-center">
            <div className="pill-accent text-base md:text-lg">
              {t("hero.badge")}
            </div>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-[#1565c0] mb-4 leading-tight max-w-4xl mx-auto">
            {t("hero.title")}
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            {t("hero.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
            <a
              href="#contact"
              className="cta-shine inline-block px-12 py-5 text-white font-bold text-lg rounded-lg shadow-lg transition-all transform hover:-translate-y-1 hover:shadow-2xl"
              style={{ backgroundColor: "#4caf50" }}
            >
              {t("hero.cta")}
            </a>
            <a
              href="#results"
              className="inline-block px-12 py-5 text-[#1565c0] font-bold text-lg rounded-lg border-2 border-[#1565c0] hover:bg-[#1565c0] hover:text-white transition-all transform hover:-translate-y-1"
            >
              {t("hero.ctaSecondary")}
            </a>
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-16 bg-white border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-gray-600 mb-8 text-lg">
            {t("trust.title")}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="flex flex-col items-center">
              <div className="text-5xl mb-3">🏠</div>
              <p className="text-[#1565c0] font-semibold">{t("trust.residential")}</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="text-5xl mb-3">🏢</div>
              <p className="text-[#1565c0] font-semibold">{t("trust.commercial")}</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="text-5xl mb-3">🏭</div>
              <p className="text-[#1565c0] font-semibold">{t("trust.industrial")}</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="text-5xl mb-3">🏛️</div>
              <p className="text-[#1565c0] font-semibold">{t("trust.government")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Inspired by "Cori in Numbers" */}
      <section id="results" className="py-20 bg-gradient-to-b from-white to-[#e8f5e9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1565c0] mb-4">
              {t("stats.title")}
            </h2>
            <p className="text-xl text-gray-700">
              {t("stats.subtitle")}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="glass-card p-8 text-center">
              <div className="text-5xl md:text-6xl font-bold text-[#4caf50] mb-4">
                {t("stats.stat1.value")}
              </div>
              <p className="text-lg text-gray-700 font-medium">
                {t("stats.stat1.label")}
              </p>
            </div>
            <div className="glass-card p-8 text-center">
              <div className="text-5xl md:text-6xl font-bold text-[#4caf50] mb-4">
                {t("stats.stat2.value")}
              </div>
              <p className="text-lg text-gray-700 font-medium">
                {t("stats.stat2.label")}
              </p>
            </div>
            <div className="glass-card p-8 text-center">
              <div className="text-5xl md:text-6xl font-bold text-[#4caf50] mb-4">
                {t("stats.stat3.value")}
              </div>
              <p className="text-lg text-gray-700 font-medium">
                {t("stats.stat3.label")}
              </p>
            </div>
            <div className="glass-card p-8 text-center">
              <div className="text-5xl md:text-6xl font-bold text-[#4caf50] mb-4">
                {t("stats.stat4.value")}
              </div>
              <p className="text-lg text-gray-700 font-medium">
                {t("stats.stat4.label")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Numbered like Cori */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1565c0] mb-4">
              {t("services.title")}
            </h2>
            <p className="text-xl text-gray-700">
              {t("services.subtitle")}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Service 01 */}
            <div
              className="glass-card p-10 border-2 border-transparent hover:border-[#4caf50] transition-all"
              style={{ backgroundColor: cardBg }}
            >
              <div className="flex items-start gap-6">
                <div className="text-6xl font-bold text-[#4caf50]/30 flex-shrink-0">
                  {t("services.solar.number")}
                </div>
                <div className="flex-grow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="text-4xl">☀️</div>
                    <h3 className="text-2xl font-bold text-[#1565c0]">
                      {t("services.solar.title")}
                    </h3>
                  </div>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    {t("services.solar.description")}
                  </p>
                </div>
              </div>
            </div>

            {/* Service 02 */}
            <div
              className="glass-card p-10 border-2 border-transparent hover:border-[#4caf50] transition-all"
              style={{ backgroundColor: cardBg }}
            >
              <div className="flex items-start gap-6">
                <div className="text-6xl font-bold text-[#4caf50]/30 flex-shrink-0">
                  {t("services.electric.number")}
                </div>
                <div className="flex-grow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="text-4xl">⚡</div>
                    <h3 className="text-2xl font-bold text-[#1565c0]">
                      {t("services.electric.title")}
                    </h3>
                  </div>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    {t("services.electric.description")}
                  </p>
                </div>
              </div>
            </div>

            {/* Service 03 */}
            <div
              className="glass-card p-10 border-2 border-transparent hover:border-[#4caf50] transition-all"
              style={{ backgroundColor: cardBg }}
            >
              <div className="flex items-start gap-6">
                <div className="text-6xl font-bold text-[#4caf50]/30 flex-shrink-0">
                  {t("services.green.number")}
                </div>
                <div className="flex-grow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="text-4xl">🌱</div>
                    <h3 className="text-2xl font-bold text-[#1565c0]">
                      {t("services.green.title")}
                    </h3>
                  </div>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    {t("services.green.description")}
                  </p>
                </div>
              </div>
            </div>

            {/* Service 04 - New */}
            <div
              className="glass-card p-10 border-2 border-transparent hover:border-[#4caf50] transition-all"
              style={{ backgroundColor: cardBg }}
            >
              <div className="flex items-start gap-6">
                <div className="text-6xl font-bold text-[#4caf50]/30 flex-shrink-0">
                  {t("services.maintenance.number")}
                </div>
                <div className="flex-grow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="text-4xl">🔧</div>
                    <h3 className="text-2xl font-bold text-[#1565c0]">
                      {t("services.maintenance.title")}
                    </h3>
                  </div>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    {t("services.maintenance.description")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-b from-white to-[#e8f5e9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1565c0] mb-4">
              {t("testimonials.title")}
            </h2>
            <p className="text-xl text-gray-700">
              {t("testimonials.subtitle")}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="glass-card p-8 border-t-4 border-[#4caf50]">
              <div className="mb-6">
                <div className="text-5xl text-[#4caf50] mb-4">"</div>
                <p className="text-gray-700 italic leading-relaxed">
                  {t("testimonials.client1.quote")}
                </p>
              </div>
              <div className="border-t border-gray-200 pt-4">
                <p className="font-bold text-[#1565c0]">
                  {t("testimonials.client1.name")}
                </p>
                <p className="text-sm text-gray-600">
                  {t("testimonials.client1.role")}
                </p>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="glass-card p-8 border-t-4 border-[#4caf50]">
              <div className="mb-6">
                <div className="text-5xl text-[#4caf50] mb-4">"</div>
                <p className="text-gray-700 italic leading-relaxed">
                  {t("testimonials.client2.quote")}
                </p>
              </div>
              <div className="border-t border-gray-200 pt-4">
                <p className="font-bold text-[#1565c0]">
                  {t("testimonials.client2.name")}
                </p>
                <p className="text-sm text-gray-600">
                  {t("testimonials.client2.role")}
                </p>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="glass-card p-8 border-t-4 border-[#4caf50]">
              <div className="mb-6">
                <div className="text-5xl text-[#4caf50] mb-4">"</div>
                <p className="text-gray-700 italic leading-relaxed">
                  {t("testimonials.client3.quote")}
                </p>
              </div>
              <div className="border-t border-gray-200 pt-4">
                <p className="font-bold text-[#1565c0]">
                  {t("testimonials.client3.name")}
                </p>
                <p className="text-sm text-gray-600">
                  {t("testimonials.client3.role")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section
        id="apropos"
        className="py-20 bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-[#1565c0] mb-8">
            {t("about.title")}
          </h2>
          <p className="text-xl text-center text-gray-700 max-w-3xl mx-auto mb-16 leading-relaxed">
            {t("about.description")}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="glass-card p-10 text-center border-t-4 border-vibrant-green">
              <div className="text-5xl mb-6">🌍</div>
              <h3 className="text-2xl font-bold text-[#1565c0] mb-4">
                {t("about.sustainability.title")}
              </h3>
              <p className="text-gray-700 leading-relaxed">{t("about.sustainability.description")}</p>
            </div>
            <div className="glass-card p-10 text-center border-t-4 border-vibrant-green">
              <div className="text-5xl mb-6">💡</div>
              <h3 className="text-2xl font-bold text-[#1565c0] mb-4">
                {t("about.innovation.title")}
              </h3>
              <p className="text-gray-700 leading-relaxed">{t("about.innovation.description")}</p>
            </div>
            <div className="glass-card p-10 text-center border-t-4 border-vibrant-green">
              <div className="text-5xl mb-6">🎓</div>
              <h3 className="text-2xl font-bold text-[#1565c0] mb-4">
                {t("about.expertise.title")}
              </h3>
              <p className="text-gray-700 leading-relaxed">{t("about.expertise.description")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-[#1565c0]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t("newsletter.title")}
          </h2>
          <p className="text-xl text-white/90 mb-8">
            {t("newsletter.subtitle")}
          </p>
          
          <newsletterFetcher.Form method="post" className="max-w-2xl mx-auto">
            <input type="hidden" name="type" value="newsletter" />
            
            {showNewsletterSuccess && (
              <div className="p-4 rounded-lg mb-6 text-center font-medium bg-green-100 text-green-800 border-2 border-green-300">
                {t("newsletter.success")}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                name="email"
                placeholder={t("newsletter.placeholder")}
                required
                disabled={isNewsletterSubmitting}
                className="flex-grow px-6 py-4 rounded-lg text-lg focus:outline-none focus:ring-4 focus:ring-[#4caf50] disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={isNewsletterSubmitting}
                className="px-10 py-4 bg-[#4caf50] text-white font-bold text-lg rounded-lg hover:bg-[#45a049] transition-all transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              >
                {t("newsletter.button")}
              </button>
            </div>
          </newsletterFetcher.Form>
        </div>
      </section>

      {/* Contact Section */}
      <section
        id="contact"
        className="py-20 bg-gradient-to-b from-white to-[#e8f5e9]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1565c0] mb-4">
              {t("contact.title")}
            </h2>
            <p className="text-xl text-gray-700">
              {t("contact.subtitle")}
            </p>
          </div>

          <fetcher.Form
            method="post"
            className="max-w-3xl mx-auto glass-card p-10"
            style={{ backgroundColor: formBg }}
          >
            <input type="hidden" name="type" value="contact" />

            {showSuccess && (
              <div className="p-4 rounded-lg mb-6 text-center font-medium bg-green-100 text-green-800 border-2 border-green-300">
                {t("contact.success", {
                  name: submittedName,
                  email: submittedEmail,
                })}
              </div>
            )}

            {fetcher.data && !fetcher.data.success && (
              <div className="p-4 rounded-lg mb-6 text-center font-medium bg-red-100 text-red-800 border-2 border-red-300">
                {fetcher.data.error || "Failed to send message. Please try again."}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label
                  htmlFor="name"
                  className="block text-[#1565c0] font-semibold mb-2 text-lg"
                >
                  {t("contact.name")}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 border-2 border-light-green rounded-lg focus:outline-none focus:border-vibrant-green disabled:opacity-50 disabled:cursor-not-allowed text-lg"
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-[#1565c0] font-semibold mb-2 text-lg"
                >
                  {t("contact.email")}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 border-2 border-light-green rounded-lg focus:outline-none focus:border-vibrant-green disabled:opacity-50 disabled:cursor-not-allowed text-lg"
                />
              </div>
            </div>

            <div className="mb-6">
              <label
                htmlFor="phone"
                className="block text-[#1565c0] font-semibold mb-2 text-lg"
              >
                {t("contact.phone")}
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                dir="ltr"
                placeholder="+216 XX XXX XXX"
                disabled={isSubmitting}
                className="w-full px-4 py-3 border-2 border-light-green rounded-lg focus:outline-none focus:border-vibrant-green disabled:opacity-50 disabled:cursor-not-allowed text-lg"
              />
            </div>

            <div className="mb-6">
              <label
                htmlFor="message"
                className="block text-[#1565c0] font-semibold mb-2 text-lg"
              >
                {t("contact.message")}
              </label>
              <textarea
                id="message"
                name="message"
                rows={6}
                required
                disabled={isSubmitting}
                className="w-full px-4 py-3 border-2 border-light-green rounded-lg focus:outline-none focus:border-vibrant-green disabled:opacity-50 disabled:cursor-not-allowed text-lg"
              ></textarea>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-10 py-5 text-white font-bold text-lg rounded-lg transition-all transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-2xl"
              style={{ backgroundColor: "#4caf50" }}
            >
              {isSubmitting ? t("contact.sending") : t("contact.submit")}
            </button>
          </fetcher.Form>

          <div
            className="glass-card p-8 text-center max-w-3xl mx-auto"
            style={{
              backgroundColor: "#ffffff",
              color: "#1f2937",
            }}
          >
            <div className="flex flex-col md:flex-row gap-8 justify-center items-center">
              <div>
                <div className="font-bold text-xl mb-2" style={{ color: "#1565c0" }}>
                  {t("contact.phoneLabel")}
                </div>
                <div dir="ltr" className="text-lg">{t("contact.phone1")}</div>
                <div dir="ltr" className="text-lg">{t("contact.phone2")}</div>
              </div>
              <div className="hidden md:block h-16 w-px bg-gray-300"></div>
              <div>
                <div className="font-bold text-xl mb-2" style={{ color: "#1565c0" }}>
                  {t("contact.emailLabel")}
                </div>
                <a
                  className="underline text-lg hover:text-[#4caf50] transition-colors"
                  style={{ color: "#1565c0" }}
                  href="mailto:contact@enervie.tn"
                  dir="ltr"
                >
                  {t("contact.emailValue")}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="text-white py-12 text-center"
        style={{ backgroundColor: "#1565c0" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-4">
            <p className="text-2xl font-semibold mb-2">{t("footer.slogan")}</p>
          </div>
          <p className="text-white/80">{t("footer.copyright")}</p>
        </div>
      </footer>
    </div>
  );
}